/** User-facing summaries; keep in sync with docs/ontology/pages when behavior changes. */
import { Language } from '../../shared/enum/EnumRepository';
import edit from '../../asset/guide/guide_edit.jpg';
import nav from '../../asset/guide/guide_nav.jpg';
import login from '../../asset/guide/guide_login.jpg';
import signup from '../../asset/guide/guide_signup.jpg';
import search from '../../asset/guide/guide_search.jpg';
import landing_1 from '../../asset/guide/guide_landing1.jpg';
import language_1 from '../../asset/guide/guide_language.jpg';
import billboard_1 from '../../asset/guide/guide_billboard1.jpg';
import projects_1 from '../../asset/guide/guide_projects.jpg';
import projectsDetail_1 from '../../asset/guide/guide_projectDetail1.jpg';
import projectsDetail_2 from '../../asset/guide/guide_projectDetail2.jpg';
import projectElement_1 from '../../asset/guide/guide_projectElement1.png';
import projectElement_2 from '../../asset/guide/guide_projectElement2.jpg';
import works_1 from '../../asset/guide/guide_works1.jpg';
import works_2 from '../../asset/guide/guide_works2.jpg';
import works_3 from '../../asset/guide/guide_works3.jpg';
import works_4 from '../../asset/guide/guide_works4.jpg';
import works_5 from '../../asset/guide/guide_works5.jpg';
import works_6 from '../../asset/guide/guide_works6.jpg';
import texts_1 from '../../asset/guide/guide_texts1.jpg';
import texts_2 from '../../asset/guide/guide_texts2.jpg';
import members_1 from '../../asset/guide/guide_members1.jpg';
import members_2 from '../../asset/guide/guide_members2.jpg';
import members_3 from '../../asset/guide/guide_members3.jpg';
import about_1 from '../../asset/guide/guide_about1.jpg';
import contact_1 from '../../asset/guide/guide_contact1.jpg';
import contact_2 from '../../asset/guide/guide_contact2.jpg';
import setting1 from '../../asset/guide/guide_setting1.jpg';
import setting2 from '../../asset/guide/guide_setting2.jpg';
import setting3 from '../../asset/guide/guide_setting3.jpg';
import setting4 from '../../asset/guide/guide_setting4.jpg';



export type GuideTabId =
  | 'overall'
  | 'landing'
  | 'billboard'
  | 'projects'
  | 'project'
  | 'works'
  | 'texts'
  | 'members'
  | 'about'
  | 'contact'
  | 'settings';

export const GUIDE_TAB_ORDER: GuideTabId[] = [
  'overall',
  'landing',
  'billboard',
  'projects',
  'project',
  'works',
  'texts',
  'members',
  'about',
  'contact',
  'settings',
];

/** `useCheckBeforeMove`의 `getCurrentPage().page` 값 → 가이드 탭 */
export function pageTypeToGuideTab(page: string): GuideTabId {
  switch (page) {
    case 'landing':
      return 'landing';
    case 'billboard':
      return 'billboard';
    case 'projects':
      return 'projects';
    case 'projectDetail':
      return 'project';
    case 'works':
      return 'works';
    case 'texts':
      return 'texts';
    case 'members':
      return 'members';
    case 'about':
      return 'about';
    case 'contact':
      return 'contact';
    case 'settings':
      return 'settings';
    default:
      return 'overall';
  }
}

export interface GuideSection {
  title: string;
  items: string[];
  imageSrc?: string;
  groupOnly?: boolean;
}

export interface GuidePageContent {
  tabLabel: string;
  /** 여러 줄이면 항목마다 `<br />`로 이어져 표시됩니다. */
  intro: string[];
  /** intro 문단 바로 아래에 표시할 이미지(정적 import URL). 없으면 생략. */
  introImages?: string[];
  sections: GuideSection[];
}

const KO: Record<GuideTabId, GuidePageContent> = {
  overall: {
    tabLabel: 'Overall',
    intro: [
      '공통으로 쓰이는 편집 모드·단축키·왼쪽 메뉴와 공개 설정의 개요입니다.',
    ],
    sections: [
      {
        title: '로그인',
        imageSrc: login,
        items: [
          '화면 오른쪽 위의 Login 버튼을 눌러 로그인할 수 있습니다.',
          '로그인되어 있으면 같은 위치에 Logout 등 계정 메뉴가 표시됩니다.',
          '로그인 창 맨 아래의 회원가입 버튼을 눌러 회원가입합니다.',
        ],
      },
      {
        title: '회원가입',
        imageSrc: signup,
        items: [
          '프로필 URL은 아카이브 주소로 활용됩니다.',
          '이메일과 비밀번호로 로그인합니다.',
          '프로필에서 Artist, Curator, Researcher, Educator, Group을 선택합니다.',
          '회원가입하면 인증 코드가 발송됩니다. 인증해야 가입이 완료됩니다.',
        ],
      },
      {
        title: '언어 변경',
        imageSrc: language_1,
        items: [
          '화면 왼쪽 아래에서 원하는 언어를 선택할 수 있습니다.',
          '기본 상태에서는 흐리게 보이지만, 마우스를 올리면 선명하게 표시됩니다.',
          'KOR과 ENG 중 원하는 언어를 선택할 수 있습니다.',
          '이 언어 설정은 방문자가 아닌, 현재 나의 화면에 적용됩니다.',
          '다른 언어도 앞으로 순차적으로 추가될 예정입니다.',
        ],
      },
      {
        title: '편집 모드',
        imageSrc: edit,
        items: [
          '페이지 소유자만 내용을 고칠 수 있고, 그렇지 않으면 항상 보기 모드입니다.',
          '화면 오른쪽 아래 연필 아이콘으로 보기 ↔ 편집을 바꿀 수 있습니다.',
          '변경하고 싶은 것이 있다면 언제나 화면 오른쪽 아래의 버튼을 찾으세요.',
        ],
      },
      {
        title: '단축키',
        items: [
          'Mac은 ⌘+E, Windows는 Ctrl+E로 편집 모드를 빠르게 변경할 수 있습니다.',
        ],
      },
      {
        title: '왼쪽 히든 메뉴',
        imageSrc: nav,
        items: [
          '왼쪽에는 Billboard·Projects·Works·Texts·About·Contact 등 여러 종류의 메뉴가 있습니다.',
          '메뉴들의 공개 여부는 Settings Page에서 설정할 수 있습니다.',
          '데스크톱에서는 화면 맨 아래의 톱니바퀴 아이콘을 눌러 Settings로 들어갑니다.',
          '데스크톱에서 톱니바퀴 왼쪽의 뒤로가기 버튼을 누르면 첫 화면으로 돌아갑니다.',
          '모바일에서는 서비스 로고를 누르면 첫 화면으로 돌아갑니다.',
        ],
      },
      {
        title: '저장과 이동',
        items: [
          '고친 내용은 페이지마다 Update, Confirm 등으로 확정해야 반영되는 경우가 많습니다.',
          '편집을 끄거나 왼쪽 메뉴·로고·프로필로 이동할 때, 저장하지 않은 내용이 있으면 안내가 뜰 수 있습니다.',
        ],
      },
    ],
  },
  landing: {
    tabLabel: 'Landing',
    intro: [
      '서비스의 첫 화면입니다.',
    ],
    sections: [
      {
        title: '검색',
        imageSrc: search,
        items: [
          '검색창에 작가 이름을 입력하면, 관련된 작가들이 검색됩니다.',
          'Searchable, Public 상태일 때만 검색 결과로 보입니다.',
          'Private 상태일 때는 검색 결과로 보이지 않습니다.'
        ],
      },
      {
        title: 'Archive Index',
        imageSrc: landing_1,
        items: [
          'Public 상태의 작가들의 목록입니다.',
          '작가의 Username을 선택하면 그 작가의 아카이브로 이동합니다.',
          'Username에 마우스를 올리면 Billboard 이미지가 미리보기로 표시됩니다.',
        ],
      },
      {
        title: 'Archive Index의 정렬 방식',
        items: [
          '영문 이름은 A–Z, 한글 이름은 초성(ㄱ·ㄴ·ㄷ …) 순으로 묶여 있습니다.',
          '검색 중에는 “검색 결과”처럼 구역이 나뉘어, 검색된 작가를 먼저 보기 쉽게 모아 둡니다.',
        ],
      },
    ],
  },
  billboard: {
    tabLabel: 'Billboard',
    intro: [
      '아카이브를 열면 가장 먼저 보이는 대표 이미지 화면입니다.',
    ],
    sections: [
      {
        title: '접근 방법',
        items: [
          '왼쪽 메뉴 바에서 Username을 누르면 해당 메뉴로 이동합니다.'
        ],
      },
      {
        title: '기본 동작',
        imageSrc: billboard_1,
        items: [
          '방문자의 경우 이미지를 누르면 보통 프로젝트 목록으로 이어집니다.',
          '소유자는 편집 모드에서 이미지를 변경하고 크기를 조절할 수 있습니다.',
          'Archive Index에서 미리보기로도 사용됩니다.',
        ],
      },
      {
        title: '비공개로 두었을 때',
        items: [
          '소유자의 경우 이미지가 흐리게 보입니다.',
          '방문자의 경우 다른 메뉴로 이동합니다.',
        ],
      },
    ],
  },
  projects: {
    tabLabel: 'Projects',
    intro: [
      '아카이브에 등록된 프로젝트를 한 화면에 목록으로 보여 줍니다.',
    ],
    introImages: [projects_1],
    sections: [
      {
        title: '목록과 보기 방식',
        items: [
          '프로젝트가 리스트로 모여 있습니다. 항목을 누르면 해당 프로젝트 상세 페이지로 들어갑니다.',
          '데스크톱에서는 티켓형, 카드형, 중앙형 세 가지 목록 보기를 전환할 수 있습니다.',
          '모바일에서는 중앙형만 지원합니다.',
        ],
      },
      {
        title: '순서와 공개',
        items: [
          '소유자는 편집 모드에서 프로젝트 순서를 바꿀 수 있습니다.',
          '프로젝트마다 공개·비공개를 정할 수 있습니다. 비공개로 둔 항목은 목록에서 흐리게 표시됩니다.',
        ],
      },
    ],
  },
  project: {
    tabLabel: 'Project',
    intro: [
      '프로젝트 및 작품 시리즈, 개인전 등을 보여주는 페이지입니다.',
    ],
    sections: [
      {
        title: '대표 이미지와 제목',
        imageSrc: projectsDetail_1,
        items: [
          '맨 위 대표 이미지를 바꾸고, 프로젝트 제목을 적습니다.',
          'Project Type을 설정할 수 있습니다. (개인전·2인전·단체전·프로젝트·시리즈 등)',
          'Year는 프로젝트 연도로, Projects 목록에서 제목 옆에 표시됩니다.',
          '표지가 화면을 세로로 얼마나 쓸지 두 가지 중에서 고를 수 있습니다.',
          'Full은 화면의 80%를 채우는 느낌, Half는 Full보다 높이가 낮습니다.',
        ],
      },
      {
        title: '기본정보와 크레딧',
        imageSrc: projectsDetail_2,
        items: [
          '“항목 이름 — 내용” 형태로 작성합니다.',
          '프로젝트의 기간, 전시 위치, 공간, 협업자, 후원처 등의 정보를 기재하는 것을 추천합니다.',
          '줄의 순서를 변경하거나 줄의 추가 및 삭제를 할 수 있습니다.',
        ],
      },
      {
        title: '프로젝트 관련 요소',
        imageSrc: projectElement_1,
        items: [
          'Work(작품 이미지): Works 메뉴에서 가져온 작품의 대표 이미지·영상과 캡션 정보입니다.',
          'Detail Cut(작품 상세 이미지): Works 메뉴에서 가져온 작품의 상세 이미지·영상과 간단한 정보입니다.',
          'Visual Aid(보조 이미지): 전시 전경, 포스터 및 자료 등 작품이 아닌 이미지를 표현할 때 사용합니다.',
          'Text(작가 노트 및 비평): Texts에서 가져온 작가 노트, 비평 등의 텍스트입니다.',
          'Document(간단한 설명): 프로젝트에 대한 간단한 설명을 표현할 때 사용합니다.',
          'PDF(관련 자료): 프로젝트와 관련된 PDF 파일입니다. 방문자가 업로드된 PDF를 다운로드할 수 있습니다.',
          'Formation(작품 배치): 여러 이미지를 포맷에 맞춰 배치할 때 사용합니다. Works에서 작품 데이터를 가져오거나 이미지를 직접 올릴 수 있습니다.',
          'Divider(구분선): 섹션을 나눌 때 사용합니다.',
        ],
      },
      {
        title: 'Works 및 Texts에서 불러오기',
        items: [
          'Work, Detail Cut, Text를 추가하려면, 먼저 Works, Texts 메뉴에서 해당 내용을 등록해 두어야 합니다.',
          '프로젝트에 넣은 Work, Detail Cut, Text는 Works·Texts List에 있는 데이터와 서로 연동되어 있습니다.',
          'Works·Texts에서 수정하면 이 프로젝트에도 반영되고, 이 프로젝트에서 수정한 내용도 Works·Texts에 동일하게 반영됩니다.',
          'Work 버튼으로 불러오기 화면을 열면 여러 Work와 Detail Cut을 한꺼번에 선택해 한 번에 넣을 수 있습니다. 중복 방지를 위해 이미 프로젝트에 넣은 Work·Detail Cut은 선택할 수 없습니다.',
          'Text 버튼으로 Texts에 등록된 글을 고른 뒤, 본문을 이루는 TextBlock 단위로 어떤 블록을 넣을지 골라 가져올 수 있습니다. 선택한 TextBlock의 순서는 프로젝트 안에서 조정할 수 있습니다.',
          'Visual Aid, Description, Divider는 프로젝트에서만 새로 만들 수 있습니다.',
        ],
      },
      {
        title: '크기와 배치 옵션',
        imageSrc: projectElement_2,
        items: [
          'Work, Visual Aid, Detail Cut의 경우 크기·위치 등의 옵션을 선택할 수 있습니다.',
          'Text의 경우 넓이·위치 등의 옵션을 선택할 수 있습니다.',
          '모든 프로젝트 요소는 순서를 변경할 수 있습니다.',
        ],
      },
      {
        title: '프로젝트 요소 삭제',
        items: [
          'Work와 Text 요소는 프로젝트에서 삭제해도 Works·Texts 페이지에 있던 원본이 사라지지는 않습니다.',
          'Visual Aid와 Document, Divider 요소는 Work, Text와 달리 삭제하면 사라집니다.',
        ],
      },
      {
        title: '포트폴리오 내보내기',
        items: [
          '프로젝트를 PDF 형식으로 생성할 수 있습니다.',
          '편집 모드 상태에서 화면 오른쪽 아래에 「포트폴리오」 버튼이 있습니다.',
          'title·username 등을 바꿔 기관·공모 제출용 포트폴리오로 사용할 수 있습니다.',
          'PDF 마지막 장에 CV 포함 여부를 선택할 수 있으며, 포함할 때는 Orig·Eng 중 CV 언어를 고를 수 있습니다.',
          'Quality에서 Low·Medium·High 중 이미지 품질을 고를 수 있습니다.',
          '모바일에서는 편집 모드 상태에서 맨 아래의 다운로드 아이콘으로 다운로드 가능합니다.',
        ],
      },
      {
        title: '작품 배치도 내보내기',
        items: [
          '전시에 사용 가능한 「작품 배치도」 를 생성할 수 있습니다.',
          '미리보기를 보며 제목·부제목·날짜·갤러리·장소 등을 적고, 평면(도면) 이미지를 올린 뒤 생성합니다.',
          'A4 크기의 세로 방향 PDF 파일로 저장되며, 가로 방향은 준비중입니다.',
          '프로젝트에 속한 Work가 순서대로 캡션(제목·연도·재료·크기 등)으로 들어가며, 한 페이지에 Work가 최대 20점까지 반영됩니다.',
          '편집 모드 상태에서 화면 오른쪽 아래에서 생성 버튼을 찾을 수 있으며, 모바일에서는 제공하지 않습니다.',
        ],
      },
    ],
  },
  works: {
    tabLabel: 'Works',
    intro: [
      '작품을 아카이빙하는 페이지입니다.',
    ],
    sections: [
      {
        title: 'List에서 보이는 값',
        imageSrc: works_1,
        items: [
          '각 행에는 대표 이미지(또는 영상 표시), 제목(Title), 크기(Size/영상의 경우 재생 시간), 재료(Material), 연도(Year), 종류(Type), 가격(Price), 소장처(Collection) 등이 열로 나뉘어 보입니다.',
          '열의 맨 위를 눌러 제목(Title)·연도(Year) 기준으로 정렬할 수 있습니다. 같은 열을 다시 누르면 오름차순·내림차순이 바뀝니다.',
          'Type·Price·Collection·Description의 노출 여부는 편집 모드에서 Overview의 각 항목 오른쪽 눈 아이콘으로 설정할 수 있습니다.',
          '10개·20개·50개 단위로 나누어 볼 수 있고, 페이지를 넘겨 List을 탐색할 수 있습니다.',
          '데스크톱에서는 왼쪽이 List, 오른쪽 Overview 영역에 선택한 작품의 상세가 펼쳐집니다.',
          '모바일에서는 List에서 고른 뒤 상세가 전체 화면으로 이어집니다.',
          '이미지 파일 여러 개 한 번에 선택해 추가할 수 있고, 데스크톱에서는 영역에 끌어다 놓기(drop)로도 넣을 수 있습니다.',
        ],
      },
      {
        title: '다중 선택',
        imageSrc: works_6,
        items: [
          'Work 여러 개를 한 번에 선택해 작업할 수 있습니다.',
          'Shift+클릭으로 기준 행부터 클릭한 행까지 구간을 선택할 수 있습니다.',
          'Ctrl+클릭(Windows)·⌘+클릭(Mac)으로 선택에 행을 더합니다. 이미 선택된 행을 다시 클릭하면 그 행만 선택에서 제외됩니다.',
          '데스크톱에서 List 영역을 드래그하면, 겹치는 행들이 함께 선택됩니다.',
          '여러 행이 선택된 상태에서 Shift+화살표(↑/↓)로 선택 범위를 늘리거나 줄일 수 있습니다.',
          '변경사항은 덮어쓰기 방식으로 적용됩니다.',
          '편집 모드 상태에서 사용할 수 있으며, 모바일에서는 제공하지 않습니다.',
        ],
      },
      {
        title: '필터',
        imageSrc: works_2,
        items: [
          '오른쪽 위 Filter를 열면 작품 종류와 제작 연도 범위를 지정해 검색 범위를 좁힐 수 있습니다.',
          '그룹 계정에서는 Member 항목으로 작품 담당 구성원별로 모아 볼 수 있습니다.',
          '모바일에서는 작품 종류 필터를 제공하지 않습니다.',
          '데스크톱에서 적용된 필터 조건은 Filter 버튼 왼쪽에 표시됩니다.',
          '표시된 필터 조건을 클릭하면 해당 조건이 제거됩니다.',
        ],
      },
      {
        title: '라벨',
        imageSrc: works_5,
        items: [
          '각 Work에 원하는 라벨을 부여해 분류, 찾기, 관리할 수 있습니다.',
          '왼쪽 위의 Label 버튼 오른쪽에 생성된 라벨을 클릭하면 해당 라벨이 부여된 Work들이 List에 나타납니다.',
          '편집 모드에서 + 아이콘으로 새 Label을 만들 수 있습니다.',
          '편집 모드에서 연필 아이콘으로 등록된 Label을 관리할 수 있습니다.',
          'Overview에서 각 Work에 부여된 Label을 확인할 수 있습니다. 또한 편집 모드에서 부여된 Label을 수정할 수 있습니다.',
        ],
      },
      {
        title: 'Overview 영역',
        imageSrc: works_3,
        items: [
          'List에서 선택한 작품의 대표 이미지, 제목, 설명, 재료, 크기, 연도, 종류, 가격, 소장처 등을 확인할 수 있습니다.',
          'Size의 오른쪽 <> 버튼을 통해 가변크기를 뜻하는 Dimensions variable을 설정할 수 있습니다.',
          'Size는 cm·in·ft·px 등 unit별로 각각 값을 저장할 수 있습니다.',
          '각 Work마다 List 등에 표시할 메인 Size unit을 설정할 수 있습니다.',
          'WorkType을 Video로 설정하면, 대표 이미지 아래에 비디오 url을 입력할 수 있는 필드가 추가되고 Size 필드도 Duration으로 변경됩니다.',
          'Video 타입의 작품은 보기 모드에서 해당 이미지를 클릭하면 새 탭으로 저장된 URL로 이동합니다.',
          'Allow Download 필드를 통해 이미지 다운로드 허용 여부를 설정할 수 있습니다.',
        ],
      },
      {
        title: 'Orig / ENG',
        items: [
          '화면 오른쪽 위의 Orig / ENG는 작품 내용의 원문·영문을 전환합니다.',
          '편집 모드에서는 제목·재료·설명을 Orig와 ENG에 각각 입력합니다.',
          '영문 내용이 비어 있으면 보기 모드에서는 Orig 내용이 대신 표시됩니다.',
          '편집 모드에서 눈 아이콘으로 방문자에게 ENG 전환을 보여 줄지 설정할 수 있습니다.',
          'Mac은 ⌘+/, Windows는 Ctrl+/로 Orig ↔ ENG를 빠르게 변경할 수 있습니다.',
        ],
      },
      {
        title: '작품 공개 설정',
        items: [
          '편집 모드에서 각 Work의 공개 여부를 설정할 수 있습니다.',
          'Private 상태의 Work는 방문자에게 보이지 않으며, 소유자의 List에서는 흐리게 표시됩니다.',
          '다중 선택을 이용하면 선택한 Work들의 공개 여부 설정을 한 번에 변경할 수 있습니다.',
          '이 설정은 Settings에서 정하는 Works 메뉴의 공개 여부와 별개입니다.',
        ],
      },
      {
        title: '디테일 컷',
        imageSrc: works_4,
        items: [
          '디테일 컷은 대표 이미지를 보충하는 작품의 상세 이미지들입니다.',
          '디테일 컷 이미지 파일을 여러 개 한 번에 선택해 추가할 수 있고, 데스크톱에서는 영역에 끌어다 놓기(drop)로도 넣을 수 있습니다.',
          '디테일 컷 간 순서를 바꿀 수 있습니다.',
          '비디오 타입으로 변경해 Video Url을 입력할 수 있습니다.',
          'Video 타입의 작품과 마찬가지로, 보기 모드에서 해당 이미지를 클릭하면 새 탭으로 저장된 URL로 이동합니다.',
        ],
      },
      {
        title: '작품 담당 구성원(Managed by)',
        groupOnly: true,
        items: [
          '그룹 계정에서는 작품마다 해당 작품의 담당 구성원을 설정할 수 있습니다.',
          'Members에서 구성원을 추가하면 담당 구성원으로 선택할 수 있는 크레딧이 함께 생성됩니다.',
          'Filter의 Member 항목에서 담당 구성원별로 작품을 모아 볼 수 있습니다.',
          'INACTIVE 상태의 구성원은 새로운 담당 구성원으로 선택할 수 없습니다.',
          '구성원을 삭제하면 해당 구성원이 담당하던 작품의 담당 구성원은 None으로 변경됩니다.',
          '구성원의 개인 계정 연결을 해제해도 작품의 담당 구성원은 그대로 유지됩니다.',
        ],
      },
      {
        title: '작품 목록 내보내기',
        items: [
          '목록 오른쪽 아래 다운로드 아이콘으로 작품 목록을 PDF/Excel 형식으로 내보낼 수 있습니다.',
          '한 번에 Work를 최대 50개까지 내보낼 수 있습니다.',
          '작품 목록 내보내기는 2단계에 걸쳐 진행됩니다.',
          '1단계: 먼저 목록에 넣을 작품을 선택하고 순서를 설정합니다.',
          '2단계: PDF 또는 Excel 형식과 언어를 선택하고, 가격·비고란 포함 여부 등을 정한 뒤 생성합니다.',
          'PDF는 Orig·ENG 언어와 가로·세로 방향을 선택하고, Title·Subtitle·Gallery를 입력할 수 있습니다.',
          'Excel은 Orig·ENG·Both 중 언어를 선택하고, 캡션을 한 줄로 표시할지 정할 수 있습니다.',
          '모바일에서는 제공하지 않습니다.',
        ],
      },
      {
        title: 'Project와 연동',
        items: [
          'Project에서 이 작품을 ProjectElement로 가져와 쓸 수 있습니다.',
          '같은 원본 데이터를 참조하므로, Works에서 내용을 바꾸면 Project를 포함해 연결된 모든 곳에 반영됩니다.',
          'Works에서 작품을 삭제하면, 해당 작품을 참조하는 모든 ProjectElement도 함께 삭제됩니다.',
        ],
      },
    ],
  },
  texts: {
    tabLabel: 'Texts',
    intro: [
      '작가노트·비평 등 텍스트 작품을 모아 두는 페이지입니다.',
    ],
    sections: [
      {
        title: '목록에서 보이는 값',
        imageSrc: texts_1,
        items: [
          '각 행에는 제목(Title), 저자(Author), 연도(Year)가 보입니다.',
          '맨 위의 열을 눌러 제목 순·연도 순으로 정렬할 수 있습니다.',
          '10개, 20개, 50개 단위로 볼 수 있고, 페이지를 이동할 수 있습니다.',
          '데스크톱에서는 왼쪽이 List, 오른쪽 Overview 영역에 선택한 글의 상세가 펼쳐집니다. 모바일에서는 List에서 고른 뒤 상세가 전체 화면으로 이어집니다.',
        ],
      },
      {
        title: '다중 선택',
        items: [
          'Text 여러 개를 한 번에 선택해 작업할 수 있습니다.',
          'Shift+클릭으로 기준 행부터 클릭한 행까지 구간을 선택할 수 있습니다.',
          'Ctrl+클릭(Windows)·⌘+클릭(Mac)으로 선택에 행을 더합니다. 이미 선택된 행을 다시 클릭하면 그 행만 선택에서 제외됩니다.',
          '데스크톱에서 List 영역을 드래그하면, 겹치는 행들이 함께 선택됩니다.',
          '여러 행이 선택된 상태에서 Shift+화살표(↑/↓)로 선택 범위를 늘리거나 줄일 수 있습니다.',
          '변경사항은 덮어쓰기 방식으로 적용됩니다.',
          '편집 모드 상태에서 사용할 수 있으며, 모바일에서는 제공하지 않습니다.',
        ],
      },
      {
        title: 'Overview 영역',
        imageSrc: texts_2,
        items: [
          'List에서 선택한 글의 제목·부제목·저자·연도 등 메타 정보와, 그 아래 본문이 이어집니다.',
          '각 글에 대한 공개/비공개 여부를 설정할 수 있습니다.',
          '비공개로 둔 글은 List에서 흐리게 보입니다.',
          '본문은 TextBlock으로 이루어져 있고 각 TextBlock은 순서를 바꿀 수 있습니다.',
        ],
      },
      {
        title: 'TextBlock 종류',
        items: [
          'PARAGRAPH: 일반 본문 문단입니다. 굵게·기울임·밑줄, S/M/L 글자 크기, 왼쪽·가운데·오른쪽 정렬을 적용할 수 있습니다.',
          'IMAGE: 글과 관련된 이미지를 추가할 수 있는 블록입니다.',
          'LINK: 링크 이름과 URL을 입력하는 블록입니다.',
        ],
      },
      {
        title: '왜 본문이 TextBlock으로 나뉘어 있나요?',
        items: [
          'Texts 메뉴의 각 글을 가져와서 Project를 구성할 수 있습니다.',
          'TextBlock으로 나뉘었기 때문에 글을 문단 별로 나누어 여러 개의 ProjectElement 에서 사용할 수 있습니다.'
        ],
      },
    ],
  },
  members: {
    tabLabel: 'Members',
    intro: [
      '그룹 구성원의 프로필을 등록하고 관리하는 그룹 계정 전용 페이지입니다.',
    ],
    sections: [
      {
        title: '목록에서 보이는 값',
        imageSrc: members_1,
        items: [
          '각 구성원의 이름과 역할(Role)이 표시됩니다.',
          '목록에서 구성원을 선택하면 오른쪽 Overview 영역에서 해당 구성원의 프로필로 스크롤합니다.',
          '편집 모드에서 화면 아래의 + 버튼으로 구성원을 추가할 수 있습니다.',
          '구성원을 추가해도 로그인 계정이나 개인 아카이브가 새로 만들어지는 것은 아닙니다.',
          '모바일에서는 별도의 목록 없이 구성원 프로필이 한 화면에 이어서 표시됩니다.',
        ],
      },
      {
        title: 'Overview 영역',
        imageSrc: members_2,
        items: [
          '각 구성원의 이름, 출생 연도, 국가, 역할, 소개, 이메일, 연락처와 SNS를 표시할 수 있습니다.',
          '이메일·연락처·SNS의 공개 여부를 설정할 수 있습니다.',
          '각 구성원 프로필의 오른쪽 상단의 "<>" 버튼을 통해 구성원의 상태를 비활성화할 수 있습니다.',
          '비활성화된 구성원은 방문자에게 표시되지 않습니다.'
        ],
      },
      {
        title: '계정 연결',
        imageSrc: members_3,
        items: [
          '각 구성원 프로필의 오른쪽 상단의 버튼을 통해 기존의 개인 계정을 연결할 수 있습니다.',
          '그룹이 개인 계정에 초대를 보내고, 개인 계정이 연결 요청을 수락하면 연결됩니다.',
          '연결은 구성원과 개인 계정이 같은 인물임을 확인하며, 구성원 프로필의 편집 권한은 그룹에 유지됩니다.',
          '연결을 승인해도 기존 구성원 프로필은 개인 계정의 정보로 대체되지 않습니다.',
          '같은 개인 계정을 여러 구성원에 연결할 수 없습니다.',
          '연결을 해제해도 구성원 프로필은 그대로 유지됩니다.',
        ],
      },
      {
        title: 'Label',
        items: [
          '화면 왼쪽 위의 Label 영역에서 구성원을 분류할 수 있습니다.',
          '각 구성원에 원하는 Label을 여러 개 부여할 수 있습니다.'
        ],
      },
    ],
  },
  about: {
    tabLabel: 'About',
    intro: [
      '기본정보와 경력사항을 보여주는 페이지입니다.',
    ],
    sections: [
      {
        title: '기본정보',
        items: [
          '이름, 출생 연도, 국가, 소개 글입니다. Orig/ENG에 따라 이름과 소개 글의 원문·영문 칸을 각각 편집합니다.',
          '이메일·전화·주소·SNS는 이 페이지가 아니라 Contact 메뉴에서 적습니다.',
        ],
      },
      {
        title: '경력사항',
        items: [
          '전시·아트페어·프레스·레지던시·소장·교육 등 종류별로 나뉩니다.',
          '연도가 한 번만 적히는 항목과, 시작·끝 연도를 함께 쓰는 항목이 있습니다.',
          '편집 모드에서 각 항목 오른쪽의 눈 아이콘으로 공개·비공개를 설정할 수 있습니다.',
          '비공개 항목은 소유자에게 흐리게 표시되며, 방문자에게는 보이지 않습니다.',
        ],
      },
      {
        title: '경력사항 추가·순서 변경',
        items: [
          '편집 모드에서 오른쪽 아래의 Career 유형를 눌러 새 경력사항을 추가할 수 있습니다. 모바일에서는 + 버튼을 누른 뒤 종류를 선택합니다.',
          '계정 종류와 활동 역할에 맞는 Career 유형이 먼저 표시되며, 아래 화살표를 누르면 나머지 종류도 볼 수 있습니다.',
          '유형 제목 옆의 체크 버튼을 누르면 그 유형의 변경사항이 한 번에 저장됩니다.',
          '각 유형 제목 옆의 순서 변경 버튼으로 경력 순서를 변경할 수 있습니다.',
          '화면 아래의 순서 변경 버튼으로 경력 유형 순서를 변경할 수 있습니다.',
        ],
      },
      {
        title: 'Orig / ENG',
        items: [
          '화면 오른쪽 위의 Orig / ENG는 화면 언어가 아니라 이름·소개 글·경력사항의 원문·영문을 전환합니다.',
          '편집 모드의 눈 아이콘으로 방문자에게 ENG 전환 버튼을 보여 줄지 설정할 수 있습니다.',
          'Mac은 ⌘+/, Windows는 Ctrl+/로 Orig ↔ ENG를 빠르게 변경할 수 있습니다.',
        ],
      },
      {
        title: '경력사항 확장',
        imageSrc: about_1,
        items: [
          '소유자는 페이지 오른쪽 위의 <> 버튼으로 경력사항을 확장할 수 있습니다.',
          '편집 모드에서 확장하면 연·월·일과 관련 URL을 입력할 수 있습니다.',
          '보기 모드에서 관련 URL이 저장된 경력사항을 클릭하면, 외부 URL은 이동 확인 후 새 탭에서 열립니다.',
          '확장된 화면은 아카이브 소유자만 볼 수 있습니다.',
        ],
      },
      {
        title: 'CV 내보내기',
        items: [
          '편집 모드에서 기본정보와 공개 상태의 경력사항을 CV 형태의 PDF로 받을 수 있습니다.',
          'Orig/ENG 언어와 가로/세로 방향을 선택한 뒤 생성합니다.',
          '설정한 경력사항과 유형 순서가 CV에 반영됩니다.',
          '데스크톱에서는 오른쪽 아래 다운로드 버튼, 모바일에서는 화면 아래 다운로드 아이콘으로 생성합니다.',
          '지원서 제출이나 공유용으로 쓰기 좋습니다.',
        ],
      },
    ],
  },
  contact: {
    tabLabel: 'Contact',
    intro: [
      '작가의 연락 정보를 보여주는 페이지입니다.',
    ],
    introImages: [contact_1],
    sections: [
      {
        title: '다루는 항목',
        items: [
          '주소, 이메일, 연락처(전화 등), Instagram·X(트위터)·Facebook, 웹사이트 URL이 항목마다 나뉘어 있습니다.',
          '원하는 항목만 채우면 되며, 사용하지 않는 항목은 눈 모양 아이콘으로 비공개로 설정하는 것을 권장합니다.',
          '변경 사항은 체크 버튼을 눌러야 저장됩니다.',
        ],
      },
      {
        title: 'Contact에 보일 항목',
        imageSrc: contact_2,
        items: [
          '항목마다 노출 여부를 정할 수 있습니다. 주소만 보이게 하고 전화는 숨기는 식으로 조합할 수 있습니다.',
          '편집 모드에서 각 항목 오른쪽의 눈 버튼으로 노출 여부를 정할 수 있습니다.',
          '숨기면 값을 적어 두었어도 방문자에게는 보이지 않습니다.',
        ],
      },
      {
        title: 'SNS·웹 링크',
        items: [
          '항목을 클릭하면 데스크톱에서는 새 탭으로 해당 페이지가 열립니다.',
          '모바일에서는 가능한 경우 해당 앱으로 연결됩니다.',
        ],
      },
    ],
  },
  settings: {
    tabLabel: 'Settings',
    intro: [
      '아카이브 설정 페이지입니다.',
      '계정 정보· 아카이브 및 메뉴 공개 설정, 검색·공유 미리보기, 구독·용량 등을 다룹니다.',
    ],
    sections: [
      {
        title: '계정·프로필 이름',
        imageSrc: setting1,
        items: [
          '주소창에 쓰이는 링크 ID(핸들), 페이지에 보이는 이름, 비밀번호 등은 버튼을 눌러 열리는 창에서 바꿉니다.',
        ],
      },
      {
        title: '공개 범위와 메뉴',
        imageSrc: setting2,
        items: [
          '아카이브 전체의 공개 수준(PRIVATE / SEARCHABLE / PUBLIC)을 고릅니다.',
          'Private 상태일 경우, 다른 사용자가 나의 Archive에 접근할 수 없습니다.',
          'Searchable 상태일 경우, 나의 Archive를 검색을 통해 접근할 수 있지만, Archive Index에 포함되지 않습니다.',
          'Public 상태일 경우, 나의 Archive를 검색을 통해 접근할 수 있고, Archive Index에 포함됩니다.',
          'Billboard·Projects·Works·Texts·About·Contact는 메뉴별로 공개 여부를 정할 수 있습니다.',
          'Members 메뉴는 그룹 계정만 사용할 수 있습니다.',
        ],
      },
      {
        title: '검색 / 공유',
        imageSrc: setting3,
        items: [
          'Page 영역의 Share에서는 링크 공유 시 미리보기에 쓰이는 부제목과 이미지를 설정합니다.',
          'Search Engine에서는 검색엔진에 쓰이는 표시 이름과 설명을 설정합니다. 공유 미리보기 제목도 이 표시 이름을 사용합니다.',
          '각 행을 열어 값을 수정한 뒤 저장합니다. 표시 이름이 비어 있으면 Username이 미리보기 제목으로 쓰입니다.',
          'Private 상태에서는 수정할 수 없습니다. Searchable 또는 Public으로 바꾼 뒤 수정할 수 있습니다.',
        ],
      },
      {
        title: '구독과 용량',
        imageSrc: setting4,
        items: [
          '저장 공간은 무료 플랜에서 500MB까지 사용할 수 있습니다.',
          '그 이상이 필요하면 월 $10으로 최대 10GB까지 이용할 수 있습니다.',
          '이용 중인 요금제·저장 공간·결제·플랜 관리는 Subscription 영역에서 다룹니다. 외부 결제 포털로 이어집니다.',
        ],
      },
      {
        title: '알림',
        items: [
          '마케팅 이메일 수신 여부는 토글로 바꾸면 바로 반영됩니다.',
        ],
      },
      {
        title: 'QR 코드',
        items: [
          '리플릿이나 명합에 추가하기 좋은 QR 코드를 PNG 파일로 다운받을 수 있습니다.',
          '배경을 투명 또는 기본으로 설정할 수 있습니다.',
          '아카이브가 검색 가능한 공개 수준일 때만 생성 가능합니다.',
        ],
      }
    ],
  },
};

const EN: Record<GuideTabId, GuidePageContent> = {
  overall: {
    tabLabel: 'Overall',
    intro: [
      'A quick tour of edit mode, keyboard shortcuts, the left-hand menu, and visibility settings—features shared across the site.',
    ],
    sections: [
      {
        title: 'Sign in',
        imageSrc: login,
        items: [
          'Use the Login button at the top right to log in.',
          'When you are signed in, Logout and other account menu items appear in the same place.',
        ],
      },
      {
        title: 'Sign up',
        imageSrc: signup,
        items: [
          'Profile URL is used as your archive address.',
          'Sign in with email and password.',
          'Choose a Profile: Artist, Curator, Researcher, Educator, or Group.',
          'Sign up sends a verification code. Confirm it to finish.',
        ],
      },
      {
        title: 'Language',
        imageSrc: language_1,
        items: [
          'You can choose your preferred language at the bottom left of the screen.',
          'By default, the button looks dim, but it becomes clear when you hover over it.',
          'You can choose between Korean and English.',
          'This language setting applies to your current view, not what visitors see.',
          'More languages will be added gradually.',
        ],
      },
      {
        title: 'Edit mode',
        imageSrc: edit,
        items: [
          'Only the page owner can edit; everyone else always sees view mode.',
          'Use the pencil icon at the bottom right to switch between view and edit.',
          'Whenever you want to make a change, look for the controls at the bottom right.',
        ],
      },
      {
        title: 'Shortcuts',
        items: [
          'Mac: ⌘+E · Windows: Ctrl+E toggles edit mode quickly.',
        ],
      },
      {
        title: 'Left-hand menu',
        imageSrc: nav,
        items: [
          'The left edge lists Billboard, Projects, Works, Texts, About, Contact, and more.',
          'Which items appear is controlled in Settings.',
          'On desktop, open Settings with the gear icon at the bottom of the screen.',
          'On desktop, the back button to the left of the gear returns to the landing page.',
          'On mobile, use the site logo to return to the landing page.',
        ],
      },
      {
        title: 'Saving and navigation',
        items: [
          'On many pages you must confirm changes with Update, Confirm, or similar before they go live.',
          'If you turn off edit mode or go elsewhere via the menu, logo, or profile, you may be warned when there are unsaved changes.',
        ],
      },
    ],
  },
  landing: {
    tabLabel: 'Landing',
    intro: [
      'The first screen when you open the service.',
    ],
    sections: [
      {
        title: 'Search',
        imageSrc: search,
        items: [
          'Enter an artist name in the search box to find matching artists.',
          'Only archives in Searchable or Public visibility appear in results.',
          'Archives set to Private never appear in search results.',
        ],
      },
      {
        title: 'Archive Index',
        imageSrc: landing_1,
        items: [
          'A directory of artists whose archives are Public.',
          'Select a username to open that artist’s archive.',
          'Hover over a username to preview that archive’s Billboard image.',
        ],
      },
      {
        title: 'How the Archive Index is sorted',
        items: [
          'Names in Latin letters are sorted A–Z; Korean names are grouped by initial consonant (ㄱ, ㄴ, ㄷ, …).',
          'While you search, results are grouped so matches are easier to find.',
        ],
      },
    ],
  },
  billboard: {
    tabLabel: 'Billboard',
    intro: [
      'The first screen inside an archive: a hero image that represents it.',
    ],
    sections: [
      {
        title: 'Access',
        items: [
          'In the left menu bar, select Username to switch to that menu.',
        ],
      },
      {
        title: 'Basics',
        imageSrc: billboard_1,
        items: [
          'Visitors usually open the Projects list by selecting the hero image.',
          'As the owner, you can replace the image and adjust its size in edit mode.',
          'In the Archive Index, it is also shown as a preview when you hover over a username.',
        ],
      },
      {
        title: 'When set to private',
        items: [
          'As the owner, you see the image dimmed.',
          'Visitors are redirected to another menu.',
        ],
      },
    ],
  },
  projects: {
    tabLabel: 'Projects',
    intro: [
      'All projects in your archive, listed on one screen.',
    ],
    introImages: [projects_1],
    sections: [
      {
        title: 'List and views',
        items: [
          'Projects are shown as a list. Select a row to open that project.',
          'On desktop, you can switch between ticket, card, and center list layouts.',
          'On mobile, only the center layout is supported.',
        ],
      },
      {
        title: 'Order and visibility',
        items: [
          'As the owner, you can reorder projects in edit mode.',
          'Each project can be public or private. Private rows appear dimmed in the list.',
        ],
      },
    ],
  },
  project: {
    tabLabel: 'Project',
    intro: [
      'A single page for a project, series, solo show, or similar.',
    ],
    sections: [
      {
        title: 'Cover image and title',
        imageSrc: projectsDetail_1,
        items: [
          'Change the cover image at the top and enter the project title.',
          'You can set Project Type (solo exhibition, duo exhibition, group exhibition, project, series, and similar).',
          'Year is the project year and appears next to the title in the Projects list.',
          'Choose how tall the cover is—two presets are available.',
          'Full fills roughly 80% of the viewport height; Half is shorter than Full.',
        ],
      },
      {
        title: 'Credits and basic info',
        imageSrc: projectsDetail_2,
        items: [
          'Use a “label — value” pattern for each line.',
          'Add dates, venue, space, collaborators, sponsors, and similar credits as needed.',
          'You can add, remove, and reorder lines.',
        ],
      },
      {
        title: 'What you can add',
        imageSrc: projectElement_1,
        items: [
          'Work: main image or video from Works, plus captions.',
          'Detail Cut: a supplementary still or clip from Works, with short info.',
          'Visual Aid: images that are not works—install shots, posters, reference material, and similar.',
          'Text: long-form writing from Texts (e.g. statements, criticism).',
          'Document: a short description block for the project.',
          'PDF: a file related to this project. Visitors can download the uploaded PDF.',
          'Formation: a block for arranging multiple images in a layout format. Import from Works or upload images directly.',
          'Divider: a line between sections.',
        ],
      },
      {
        title: 'Importing from Works and Texts',
        items: [
          'To add Work, Detail Cut, or Text, add it in Works or Texts first.',
          'Work, Detail Cut, and Text you add here stay linked to the Works and Texts lists.',
          'Changes in Works or Texts show up in this project, and changes you make here update Works and Texts too.',
          'Use the Work button to import several Work items and Detail Cuts at once. You cannot select a Work or Detail Cut that is already in this project.',
          'Use the Text button to choose a text in Texts, pick TextBlocks to include, and set their order in the project.',
          'You can only create Visual Aid, Description, and Divider on the project page.',
        ],
      },
      {
        title: 'Layout options',
        imageSrc: projectElement_2,
        items: [
          'For Work, Visual Aid, and Detail Cut you can adjust size and placement.',
          'For Text you can adjust width and placement.',
          'You can reorder every block.',
        ],
      },
      {
        title: 'Removing items',
        items: [
          'Removing Work or Text from the project does not delete the original in Works or Texts.',
          'Visual Aid, Document, and Divider exist only in the project—deleting them removes them for good.',
        ],
      },
      {
        title: 'Portfolio export',
        items: [
          'Generate the project as a PDF.',
          'In edit mode, the Portfolio button is at the bottom right.',
          'Edit title, username, and similar fields for portfolios for institutions or open calls.',
          'You can choose whether to include a CV on the last page of the PDF. If included, choose CV language as Orig or Eng.',
          'Choose image Quality: Low, Medium, or High.',
          'On mobile, in edit mode, use the download icon in the bottom bar.',
        ],
      },
      {
        title: 'Floor plan export',
        items: [
          'Create a floor plan PDF for exhibitions.',
          'Use the preview: enter title, subtitle, date, gallery, location, upload the plan image, then generate.',
          'Output is A4 portrait. Landscape is not available yet.',
          'Work elements become captions in order (title, year, materials, size, etc.), up to 20 works on one sheet.',
          'In edit mode, the control is at the bottom right; not available on mobile.',
        ],
      },
      {
        title: 'Back to Projects and top of page',
        items: [
          'At the bottom right of the project element list, two icon buttons appear.',
          'Back to Projects (left-pointing arrow): returns to this archive’s Projects list.',
          'Back to top (up arrow): smoothly scrolls the project detail page to the top (cover and title).',
        ],
      },
    ],
  },
  works: {
    tabLabel: 'Works',
    intro: [
      'The page where you archive your works.',
    ],
    sections: [
      {
        title: 'What appears in the list',
        imageSrc: works_1,
        items: [
          'Each row shows a thumbnail (or video), Title, Size (duration for video), Material, Year, Type, Price, Collection, and more.',
          'Select the Title or Year column header to sort; select the same column again to switch ascending / descending order.',
          'In edit mode, use the eye icon next to Type, Price, Collection, or Description in the Overview to show or hide that field.',
          'Browse 10, 20, or 50 rows per page and use pagination to move through the list.',
          'On desktop, the list is on the left and the Overview panel on the right shows the selected work.',
          'On mobile, selecting a row opens the detail view full screen.',
          'You can add several image files at once; on desktop, you can also drag files into the drop area.',
        ],
      },
      {
        title: 'Multi-select',
        imageSrc: works_6,
        items: [
          'Select several works at once to work on them together.',
          'Shift-click to select from the anchor row through the row you click.',
          'Ctrl-click (Windows) or ⌘-click (Mac) adds a row; click a selected row again to remove just that row.',
          'On desktop, drag over the list area—every row touched by the box is selected.',
          'With multiple rows selected, Shift+↑/↓ extends or shrinks the range.',
          'Changes apply as overwrites.',
          'Edit mode on desktop only; not available on mobile.',
        ],
      },
      {
        title: 'Filter',
        imageSrc: works_2,
        items: [
          'Open Filter at the top right to narrow the list by work type and production year range.',
          'Group accounts can also use Member to view works by assigned Group Member.',
          'The work type filter is not available on mobile.',
          'On desktop, active filters appear to the left of the Filter button.',
          'Select an active filter to remove that condition.',
        ],
      },
      {
        title: 'Labels',
        imageSrc: works_5,
        items: [
          'Label works to sort, find, and manage them.',
          'Next to the Label control (top left), click a label to show only works with that label in the list.',
          'In edit mode, + adds a new label.',
          'In edit mode, the pencil opens label management.',
          'The Overview shows each work’s labels; in edit mode you can change them.',
        ],
      },
      {
        title: 'Overview panel',
        imageSrc: works_3,
        items: [
          'For the selected work you see the main image, title, description, materials, size, year, type, price, collection, and more.',
          'Use the <> control next to Size to mark dimensions as variable (Dimensions variable).',
          'You can save separate size values for each unit (cm, in, ft, px, and similar).',
          'For each work, you can set the main size unit shown in the list and in view mode.',
          'Set the work type to Video to show a video URL under the main image and turn Size into Duration.',
          'For video works, in view mode select the image to open the saved URL in a new tab.',
          'Use Allow Download to control whether visitors can save images.',
        ],
      },
      {
        title: 'Orig / ENG',
        items: [
          'Orig / ENG at the top right switches the original and English of the work content.',
          'In edit mode, enter the Title, Material, and Description separately in Orig and ENG.',
          'If English content is empty, the Orig content appears instead in view mode.',
          'In edit mode, use the eye icon to choose whether to show the ENG switch to visitors.',
          'Use ⌘+/ on Mac or Ctrl+/ on Windows to switch quickly between Orig and ENG.',
        ],
      },
      {
        title: 'Work visibility',
        items: [
          'In edit mode, you can set whether each work is public.',
          'A Private work is hidden from visitors and appears dimmed in the owner’s list.',
          'With multi-select, you can change visibility for the selected works at once.',
          'This is separate from the visibility of the Works menu set in Settings.',
        ],
      },
      {
        title: 'Detail shots',
        imageSrc: works_4,
        items: [
          'Detail shots are extra images (or clips) that support the main image.',
          'Add several files at once; on desktop you can drag them into the area.',
          'Reorder detail shots as you like.',
          'You can set a shot to video and enter a video URL.',
          'As with main video works, in view mode select the image to open the URL in a new tab.',
        ],
      },
      {
        title: 'Assigned Group Member (Managed by)',
        groupOnly: true,
        items: [
          'For each work in a group account, you can set the Group Member responsible for it.',
          'Adding a Group Member in Members also creates a credit that can be selected as the assigned Group Member.',
          'Use Member in Filter to view works by assigned Group Member.',
          'An INACTIVE Group Member cannot be newly assigned to a work.',
          'Deleting a Group Member changes the assigned Group Member to None for works they were responsible for.',
          'Disconnecting a Group Member’s personal account does not change the Group Member assigned to a work.',
        ],
      },
      {
        title: 'Work list export',
        items: [
          'Use the download icon at the bottom right of the list to export your work list as PDF or Excel.',
          'You can export up to 50 works at a time.',
          'Export runs in two steps.',
          'Step 1: Select the works to include and set their order.',
          'Step 2: Choose PDF or Excel and the content language, then decide whether to include price and note columns before generating the file.',
          'For PDF, choose Orig or ENG and portrait or landscape, and enter a Title, Subtitle, and Gallery.',
          'For Excel, choose Orig, ENG, or Both and decide whether captions appear on a single line.',
          'Not available on mobile.',
        ],
      },
      {
        title: 'Linking with Projects',
        items: [
          'You can add this work to a project.',
          'It stays linked to this catalog entry: edits in Works appear everywhere the work is used, including in projects.',
          'If you delete the work in Works, it is removed from every project that used it.',
        ],
      },
    ],
  },
  texts: {
    tabLabel: 'Texts',
    intro: [
      'A page for artist notes, criticism, and other writing.',
    ],
    sections: [
      {
        title: 'What appears in the list',
        imageSrc: texts_1,
        items: [
          'Each row shows Title, Author, and Year.',
          'Select a column header to sort by title or year.',
          'Browse 10, 20, or 50 items per page and use pagination to move between pages.',
          'On desktop, the list is on the left and the Overview panel on the right; on mobile, selecting a row opens the detail view full screen.',
        ],
      },
      {
        title: 'Multi-select',
        items: [
          'Select several texts at once to work on them together.',
          'Shift-click to select from the anchor row through the row you click.',
          'Ctrl-click (Windows) or ⌘-click (Mac) adds a row; click a selected row again to remove just that row.',
          'On desktop, drag over the list area—every row touched by the box is selected.',
          'With multiple rows selected, Shift+↑/↓ extends or shrinks the range.',
          'Changes apply as overwrites.',
          'Edit mode on desktop only; not available on mobile.',
        ],
      },
      {
        title: 'Overview panel',
        imageSrc: texts_2,
        items: [
          'For the selected text you see title, subtitle, author, year, and the body below.',
          'Each text can be public or private.',
          'Private rows look dimmed in the list.',
          'The body is made of TextBlocks; you can reorder them.',
        ],
      },
      {
        title: 'TextBlock types',
        items: [
          'PARAGRAPH: body copy with bold, italic, underline, S/M/L text sizes, and left, center, or right alignment.',
          'IMAGE: a block for adding images related to the text.',
          'LINK: a block for entering a link label and URL.',
        ],
      },
      {
        title: 'Why TextBlocks?',
        items: [
          'You can bring texts from Texts into projects.',
          'With TextBlocks, you can reuse different parts of the same text in more than one project.',
        ],
      },
    ],
  },
  members: {
    tabLabel: 'Members',
    intro: [
      'A Group-accounts-only page for adding and managing Group Member profiles.',
    ],
    sections: [
      {
        title: 'What appears in the list',
        imageSrc: members_1,
        items: [
          'Each Group Member’s name and Role appear in the list.',
          'Select a Group Member in the list to scroll to that Group Member’s profile in the Overview area on the right.',
          'In edit mode, use the + button at the bottom to add a Group Member.',
          'Adding a Group Member does not create a login account or a personal archive.',
          'On mobile, there is no separate list; Group Member profiles appear one after another on the same page.',
        ],
      },
      {
        title: 'Overview area',
        imageSrc: members_2,
        items: [
          'The Overview shows each Group Member’s name, birth year, country, role, bio, email, contact details, and social links.',
          'Set visibility separately for email, contact details, and social links.',
          'Use the <> button at the top right of each profile to set the Group Member to INACTIVE.',
          'INACTIVE Group Members are hidden from visitors.',
        ],
      },
      {
        title: 'Connecting a personal account',
        imageSrc: members_3,
        items: [
          'Use the button at the top right of a Group Member profile to connect an existing personal account.',
          'The group sends an invitation, and the connection is created when the personal account accepts it.',
          'A connection confirms that the Group Member and personal account represent the same person; the Group retains editing control.',
          'Accepting a connection does not replace the existing Group Member profile with personal account information.',
          'The same personal account cannot be connected to more than one Group Member.',
          'Disconnecting the account leaves the Group Member profile unchanged.',
        ],
      },
      {
        title: 'Labels',
        items: [
          'Use the Label area at the top left to categorize Group Members.',
          'Assign multiple labels to each Group Member.',
        ],
      },
    ],
  },
  about: {
    tabLabel: 'About',
    intro: [
      'A page that shows basic information and career history.',
    ],
    sections: [
      {
        title: 'Basic information',
        items: [
          'Name, birth year, country, and bio. Edit Orig and ENG separately.',
          'Email, phone, address, and social links go in the Contact menu.',
        ],
      },
      {
        title: 'Career history',
        items: [
          'Grouped by type: exhibitions, art fairs, press, residencies, collections, teaching, and more.',
          'Some entries use one year; others use start and end years.',
          'In edit mode, use the eye icon to the right of each entry for public or private.',
          'Private entries look dimmed to the owner and are hidden from visitors.',
        ],
      },
      {
        title: 'Adding and reordering career history',
        items: [
          'In edit mode, press a Career type at the bottom right to add an entry. On mobile, press + and choose a type.',
          'Career types matching your account type and activity role appear first. Press the down arrow for the rest.',
          'Press the check button next to a type title to save that type at once.',
          'Use the reorder button next to a type title to change career order.',
          'Use the reorder button at the bottom to change type order.',
        ],
      },
      {
        title: 'Orig / ENG',
        items: [
          'Orig / ENG at the top right switches original and English for name, bio, and career—not the screen language.',
          'In edit mode, use the eye icon to show or hide the ENG switch for visitors.',
          '⌘+/ (Mac) or Ctrl+/ (Windows) switches Orig ↔ ENG.',
        ],
      },
      {
        title: 'Career expansion',
        imageSrc: about_1,
        items: [
          'As the owner, use <> at the top right to expand career history.',
          'In edit mode, expand to enter year, month, day, and a related URL.',
          'In view mode, click an entry with a related URL. External links open in a new tab after you confirm.',
          'Only the archive owner can see the expanded view.',
        ],
      },
      {
        title: 'CV export',
        items: [
          'In edit mode, download a CV PDF of basic information and public career history.',
          'Choose Orig or ENG and landscape or portrait, then generate.',
          'Career history and type order appear in the CV.',
          'Desktop: download button at the bottom right. Mobile: download icon at the bottom.',
          'Useful for applications and sharing.',
        ],
      },
    ],
  },
  contact: {
    tabLabel: 'Contact',
    intro: [
      'A page that shows the artist’s contact information.',
    ],
    introImages: [contact_1],
    sections: [
      {
        title: 'Fields on this page',
        items: [
          'Address, email, phone, Instagram, X (Twitter), Facebook, and website URL each have their own field.',
          'Use only the fields you need; hide unused fields with the eye icon when possible.',
          'Changes are saved only when you press the check button.',
        ],
      },
      {
        title: 'What visitors see',
        imageSrc: contact_2,
        items: [
          'You can set visibility per field—for example, show your address but hide your phone.',
          'In edit mode, use the eye button to the right of each field to turn visibility on or off.',
          'When a field is hidden, visitors do not see it even if you have saved content there.',
        ],
      },
      {
        title: 'Social and web links',
        items: [
          'On desktop, selecting a field opens the link in a new tab.',
          'On mobile, the link may open in the relevant app when available.',
        ],
      },
    ],
  },
  settings: {
    tabLabel: 'Settings',
    intro: [
      'Your archive settings.',
      'Account, visibility, menus, search and share previews, subscription, storage, and more.',
    ],
    sections: [
      {
        title: 'Account and display name',
        imageSrc: setting1,
        items: [
          'Change your link ID (handle), public name, and password from the buttons that open a window for each.',
        ],
      },
      {
        title: 'Visibility and menus',
        imageSrc: setting2,
        items: [
          'Set the archive-wide visibility: PRIVATE, SEARCHABLE, or PUBLIC.',
          'Private: others cannot open your archive.',
          'Searchable: your archive appears in search but not in the Archive Index.',
          'Public: your archive appears in search and in the Archive Index.',
          'Choose whether Billboard, Projects, Works, Texts, About, and Contact appear in the menu.',
        ],
      },
      {
        title: 'Share / Search Engine',
        imageSrc: setting3,
        items: [
          'Under Page, Share sets the subtitle and preview image used when someone shares your link.',
          'Search Engine sets the display names and description used for search engines. The share preview title also uses these display names.',
          'Open each row to edit the values, then save. If the display names are empty, your Username is used for the preview title.',
          'Private visibility blocks this; set visibility to Searchable or Public to edit.',
        ],
      },
      {
        title: 'Subscription and storage',
        imageSrc: setting4,
        items: [
          'You get 500 MB of storage on the free tier.',
          'Need more? Up to 10 GB is available for $10 per month.',
          'Manage plan, usage, billing, and upgrades in Subscription; it opens an external payment page.',
        ],
      },
      {
        title: 'Notifications',
        items: [
          'Marketing email uses a single toggle; changes apply immediately.',
        ],
      },
      {
        title: 'QR code',
        items: [
          'Download a PNG QR code—handy for flyers or business cards.',
          'Choose a transparent or white background.',
          'QR codes are available only when the archive is visible enough for search and listing.',
        ],
      },
    ],
  },
};

export function getGuidePage(lang: Language, tab: GuideTabId): GuidePageContent {
  return lang === Language.KR ? KO[tab] : EN[tab];
}
